import {
  MockHook,
  onlyUnique,
  PermissionType,
  usePermissionEntitySearch,
} from '@jupiterone/jupiter-web-toolkit';
import { useState, useMemo } from 'react';
import asArray from 'navbar/src/jupiterAccountSettings/AccountSettings/UsersAccess/GroupDetail/QueryPermissions/asArray';

export interface EditPermissionDialogProps {
  title: string;
  permissionSet: { [key: string]: string[] };
  onCancel: () => void;
  onSave: (permissionSet: any) => void;
  saving?: boolean;
}

const useEditPermissionDialogLogic = ({
  permissionSet,
  title,
  saving = false,
  onCancel,
  onSave,
}: EditPermissionDialogProps) => {
  const permissionTypes = onlyUnique([
    ...Object.values(PermissionType),
    ...Object.keys(permissionSet),
  ]);

  const [tempPermissionSet, setTempPermissionSet] = useState(permissionSet);
  const [selectedPermissionFilter, setSelectedPermissionFilter] = useState(
    permissionTypes[0]
  );
  const [filterValue, setFilterValue] = useState('');

  const permissionEntities = usePermissionEntitySearch(
    selectedPermissionFilter
  );
  const permissionSetValues = useMemo(() => {
    return Object.entries(tempPermissionSet).map(([k, v]): [
      string,
      string[]
    ] => [k, asArray<string>(v)]);
  }, [tempPermissionSet]);
  const permissionAutocomplete = useMemo(() => {
    const values = new Set<string | boolean>();
    permissionEntities.data?.forEach((item) => {
      values.add(item.value);
      if (item.displayName) values.add(item.displayName);
    });
    return [...values];
  }, [permissionEntities]);
  const handlePermissionValue = () => {
    setTempPermissionSet((pSet) => {
      const newSet = { ...pSet };
      newSet[selectedPermissionFilter] = [
        ...asArray(newSet[selectedPermissionFilter]),
        filterValue,
      ];
      return newSet;
    });
    setFilterValue('');
  };

  const handleSave = () => onSave(tempPermissionSet);
  const handlePermissionFilterChange = (value: string) => {
    const clearFilter = value !== selectedPermissionFilter;
    setSelectedPermissionFilter(value);
    if (clearFilter) setFilterValue('');
  };
  const handleFilterValue = (_e: any, newValue: string) => {
    setFilterValue(newValue);
  };

  const handleDeletePermissionValue = (field: string, index: number) => () => {
    setTempPermissionSet((pSet) => {
      const newSet = { ...pSet };
      const newValues = asArray(newSet[field]).filter((_v, i) => i !== index);
      if (newValues.length === 0) {
        delete newSet[field]; // Delete whole field if empty
      } else {
        newSet[field] = newValues; // Update field
      }
      return newSet;
    });
  };

  return {
    saving,
    title,
    permissionSetValues,
    permissionTypes,
    selectedPermissionFilter,
    filterValue,
    permissionAutocomplete,
    permissionEntities,
    handleCancel: onCancel,
    handleSave,
    handlePermissionFilterChange,
    handleFilterValue,
    handlePermissionValue,
    handleDeletePermissionValue,
  };
};

export type EditPermissionDialogComponentProps = ReturnType<
  typeof useEditPermissionDialogLogic
>;
export const MOCK_EDIT_PERMISSION_DIALOG_PROPS: EditPermissionDialogComponentProps = {
  saving: false,
  title: 'Edit Permission Set #1',
  permissionSetValues: [['_type', ['action']]],
  permissionTypes: [...Object.values(PermissionType), 'aCustomType'],
  selectedPermissionFilter: 'aCustomType',
  filterValue: '',
  permissionAutocomplete: ['123-345', '456-789', false],
  permissionEntities: MockHook.mockAsyncFunction,
  handleCancel: MockHook.mockFunction,
  handleSave: MockHook.mockFunction,
  handlePermissionFilterChange: MockHook.mockFunction,
  handleFilterValue: MockHook.mockFunction,
  handlePermissionValue: MockHook.mockFunction,
  handleDeletePermissionValue: MockHook.mockFunction,
};
export default MockHook.useMockHook(
  useEditPermissionDialogLogic,
  MOCK_EDIT_PERMISSION_DIALOG_PROPS
);
